import React, { FC, useEffect, useState } from 'react';
import { User } from '../../interfaces/User';
import styled from 'styled-components';
import { DefaultInput } from '../general/forms/DefaultInput';
import { Toggle } from '../general/forms/Toggle';
import { DropDown } from '../general/forms/DropDown';
import { TextBox } from '../general/forms/TextBox';
import { SelectBox } from '../general/forms/SelectBox';
import { LinkButton } from '../general/LinkButton';
import { useShowAlertPopup } from '../../state/hooks/AlertHooks';
import { FormHeadline, FormWrapper, FormContainer, FormFieldset, FormButtonWrapper } from './Layouts';
import { CustomerTypes } from '../../utils/CustomerTypes';
import { GenericList } from '../lists/GenericList';
import { ProductCategory } from '../../interfaces/Product';
import {
  AdditionalUserProductPermissionsListContent,
  ProductWithPermission,
} from '../lists/AdditionalUserProductPermissionsListContent';
import { AdditionalUserProductPermissionsSelectAllListContent } from '../lists/AdditionalUserProductPermissionsSelectAllListContent';
import { deepCopy } from '../../utils/deepUtils';
import { useApiDispatchQueue } from '../../state/ApiDispatchState';
import * as AuthStore from '../store/AuthStore';

export type OnChangeUserForm = {
  user: User;
  permissions: any[] | null
}

export interface UserFormProps {
  user?: User;
  onChange: (payload: OnChangeUserForm) => void;
  onCancel: () => void;
}


export interface UserFromValidationState {
  email: boolean;
  phone: boolean;
  role: boolean;
  customerTypes: boolean;
  emailSignature: boolean;
  dewDepartment: boolean;
  firstName: boolean;
  familyName: boolean;
  password: boolean;
}

const Headline = styled.h2`
  display: flex;
  flex-direction: row;
  margin-bottom: 1.5rem;
`;

export interface UserProductPermissionProps {
  id: string;
  userId: string;
  productId: string;
  hasLeadPermission: boolean;
  hasSampleContractPermission: boolean;
  hasContractPermission: boolean;
}

const CheckboxWrapper = styled.div`
 display: flex;
 flex-direction: row;
 justify-content: space-between;
 width: 100%;
`;

export interface ProductCategoryWithProductAndPermissions {
  name: string;
  id: string;
  lead: string;
  contract: string;
  sampleContract: string;
  products: ProductWithPermission[];
}

export const UserForm: FC<UserFormProps> = (props) => {
  const { user = null, onChange, onCancel } = props;
  const dispatchApiCall = useApiDispatchQueue();
  const showNotificationBanner = useShowAlertPopup();
  const [tempUser, setTempUser] = useState<User | null>(user);
  const [permissions, setPermissions] = useState<ProductCategoryWithProductAndPermissions[] | null>(null);
  const [tempPermissions, setTempPermissions] = useState<ProductCategoryWithProductAndPermissions[] | null>(null);
  const [validationState, setValidationState] = useState<UserFromValidationState>({
    customerTypes: false,
    dewDepartment: false,
    email: false,
    emailSignature: false,
    familyName: false,
    firstName: false,
    password: false,
    phone: false,
    role: false,
  });

  const validate = () => {
    const state = {
      emailSignature: (tempUser?.emailSignature && tempUser?.emailSignature.length > 0) ? false : true,
      customerTypes: (tempUser?.customerTypes && tempUser?.customerTypes?.length > 0) ? false : true,
      dewDepartment: (tempUser?.dewDepartment && tempUser?.dewDepartment.length > 0) ? false : true,
      email: (tempUser?.email && tempUser?.email.length > 0) ? false : true,
      familyName: (tempUser?.familyName && tempUser?.familyName.length > 0) ? false : true,
      firstName: (tempUser?.firstName && tempUser?.firstName.length > 0) ? false : true,
      password: user !== null ? false : (tempUser && tempUser.password && tempUser.password.length > 0 ? false : true),
      phone: (tempUser?.phone && tempUser?.phone.length > 0) ? false : true,
      role: (tempUser?.role && tempUser?.role === 0 || 1 || 2) ? false : true,
    };
    setValidationState(state);
    return state;
  };

  const toggleAllPermissionsFromCategoryByType = (type: string, category: ProductCategoryWithProductAndPermissions) => {
    const currentCat = tempPermissions.filter((cat) => cat.id === category.id)[0];
    const activeProductsCount = currentCat.products.filter((product) => product.permissions[type]).length;
    const activateAll = Boolean(!(activeProductsCount === currentCat.products.length));
    const currentCatProducts = currentCat.products.map((product) => {
      const temp = { ...product };
      temp.permissions[type] = activateAll;
      return temp;
    });

    const result = [...tempPermissions.filter((cat) => cat.id !== category.id), {
      ...currentCat,
      products: currentCatProducts,
    }];

    setTempPermissions(result);
  };

  const toggleAllPermissionsByType = (type: string, active: boolean) => {
    setTempPermissions(tempPermissions.map((category) => {
      return {
        ...category,
        products: category.products.map((product) => {
          const result = product;
          result.permissions[type] = active;
          return result;
        }),
      };
    }));
  };

  useEffect(() => {
    if (user && user.id) {
      dispatchApiCall({
        method: 'get',
        route: `/api/users/permission/${user.id}`,
        header: AuthStore.getAuthHeader(),
        onSuccess: (response) => {
          dispatchApiCall({
            method: 'get',
            route: `/api/product/category/all`,
            header: AuthStore.getAuthHeader(),
            onSuccess: (innerResponse) => {
              const productPermissions = response.data;
              const categories = innerResponse.data;

              const result = categories.map((category: ProductCategory) => {
                return {
                  name: category.name,
                  id: category.id,
                  lead: 'Lead',
                  contract: 'Vertrag',
                  sampleContract: 'Mustervertrag',
                  products: category.products.map((product) => {
                    const permission = productPermissions ? productPermissions.filter((permission: any) => permission.productId === product.id) : null;
                    return {
                      id: product.id,
                      name: product.name,
                      permissions: (permission && permission[0]) ? { ...permission[0] } : null,
                    };
                  }) || [],
                };
              });

              setPermissions(result);
            },
          });
        },
      });
    }
  }, [user]);

  useEffect(() => {
    if (permissions && permissions.length > 0) {
      setTempPermissions(deepCopy(permissions));
    }
  }, [permissions]);

  return (
    <>
      <FormHeadline>Benutzer {user === null ? 'Hinzufügen' : 'Bearbeiten'}</FormHeadline>
      <FormWrapper>
        <FormContainer>
          <FormFieldset>
            <FormHeadline>Stammdaten</FormHeadline>
            <DefaultInput
              id={'fistName'}
              onChange={(event) => {
                setTempUser({ ...tempUser, firstName: event.target.value });
              }}
              type={'text'}
              label={'Vorname'}
              placeholder={'Vorname'}
              value={tempUser?.firstName}
              required={true}
              hasError={validationState.firstName} />
            <DefaultInput
              id={'familyName'}
              onChange={(event) => {
                setTempUser({ ...tempUser, familyName: event.target.value });
              }}
              type={'text'}
              label={'Nachname'}
              placeholder={'Nachname'}
              value={tempUser?.familyName}
              required={true}
              hasError={validationState.familyName} />
            <DefaultInput
              id={'email'}
              onChange={(event) => {
                setTempUser({ ...tempUser, email: event.target.value });
              }}
              type={'text'}
              label={'E-Mail'}
              placeholder={'max.mustermann@dew21.de'}
              value={tempUser?.email}
              required={true}
              hasError={validationState.email} />

            {
              // show password field only for new users
            }
            {
              user === null && <DefaultInput
                id={'password'}
                onChange={(event) => {
                  setTempUser({ ...tempUser, password: event.target.value });
                }}
                type={'password'}
                label={'Passwort'}
                required={true} />
              //hasError={validationState.password}

            }
            <DefaultInput
              id={'phone'}
              onChange={(event) => {
                setTempUser({ ...tempUser, phone: event.target.value });
              }}
              type={'text'}
              label={'Telefon'}
              placeholder={'+49123456789'}
              value={tempUser?.phone}
              required={true}
              hasError={validationState.phone} />
          </FormFieldset>
          <FormFieldset>
            <FormHeadline>Danger Zone</FormHeadline>
            <DefaultInput
              id={'dew-department'}
              onChange={(event) => {
                setTempUser({ ...tempUser, dewDepartment: event.target.value });
              }}
              type={'text'}
              label={'Abteilung'}
              placeholder={''}
              value={tempUser?.dewDepartment}
              required={true}
              hasError={validationState.dewDepartment} />
            <Toggle
              id={'locked'}
              label={'Gesperrt'}
              value={tempUser?.locked}
              onChange={(checked) => {
                setTempUser({ ...tempUser, locked: checked });
              }} />
            <DropDown
              id={'role'}
              onChange={(event) => {
                setTempUser({ ...tempUser, role: parseInt(event.target.selectedOptions[0].value) });
              }}
              type={'text'}
              label={'Rolle'}
              value={tempUser?.role}
              required={true}
              hasError={validationState.role}
              options={[
                { key: 0, value: 'Administrator' },
                { key: 1, value: 'Vertriebsleiter' },
                { key: 2, value: 'Vertriebsmitarbeiter' },
              ]} />
            <TextBox
              id={'email-signature'}
              onChange={(event) => {
                setTempUser({ ...tempUser, emailSignature: event.target.value });
              }}
              label={'E-Mail Signature'}
              placeholder={''}
              value={tempUser?.emailSignature}
              required={true}
              hasError={validationState.emailSignature} />
            <SelectBox
              multi={true}
              id={'customer-types'}
              onChange={(data: string[]) => {
                setTempUser({ ...tempUser, customerTypes: data });
              }}
              label={'Zielgruppen'}
              value={tempUser?.customerTypes || []}
              options={[
                { key: CustomerTypes.BUSINESS, value: 'Gewerbekunden', icon: 'customers-commercial' },
                { key: CustomerTypes.HOUSING_INDUSTRY, value: 'Wohnungswirtschaft', icon: 'customers-housing' },
                { key: CustomerTypes.MAJOR_CUSTOMERS, value: 'Großkunden', icon: 'customers-major' },
              ]}
              required={true}
              hasError={validationState.customerTypes} />
          </FormFieldset>
        </FormContainer>
      </FormWrapper>
      {
        (tempPermissions && tempPermissions.length > 0) && <FormFieldset>
          <Headline>Berechtigungen</Headline>

          <GenericList
            collapsible={true}
            columns={[
              { name: 'name', displayName: 'x', sortable: false },
            ]}
            items={[{
              name: 'Alle an/abwählen',
            }]}
            additionalRenderFn={() => {
              return <AdditionalUserProductPermissionsSelectAllListContent
                permissions={tempPermissions}
                toggleAllLeads={(checked) => toggleAllPermissionsByType('hasLeadPermission', checked)}
                toggleAllContracts={(checked) => toggleAllPermissionsByType('hasContractPermission', checked)}
                toggleAllSampleContracts={(checked) => toggleAllPermissionsByType('hasSampleContractPermission', checked)} />;
            }} />

          <GenericList
            collapsible={true}
            displayColumnNames={false}
            columns={[
              { name: 'name', displayName: 'Produkt', sortable: false },
              { name: 'lead', displayName: 'Lead', sortable: false },
              { name: 'sampleContract', displayName: 'Mustervertrag', sortable: false },
              { name: 'contract', displayName: 'Vertrag', sortable: false },
            ]}
            items={tempPermissions}
            additionalRenderFn={(category) => {
              return <AdditionalUserProductPermissionsListContent
                category={category}
                togglePermission={(payload) => {
                  setTempPermissions(tempPermissions.map((category) => {
                    return {
                      ...category,
                      products: category.products.map((product) => {
                        if (product.id === payload.productId) {
                          return { ...product, permissions: payload };
                        } else {
                          return product;
                        }
                      }),
                    };
                  }));
                }}
                toggleAllLeads={() => {
                  toggleAllPermissionsFromCategoryByType('hasLeadPermission', category);
                }}
                toggleAllContracts={() => {
                  toggleAllPermissionsFromCategoryByType('hasContractPermission', category);
                }}
                toggleAllSampleContracts={() => {
                  toggleAllPermissionsFromCategoryByType('hasSampleContractPermission', category);
                }} />;
            }} />
        </FormFieldset>
      }
      <FormButtonWrapper>
        <LinkButton onChangeHandler={(event: React.FormEvent<HTMLButtonElement>) => {
          onCancel();
        }}>Abbrechen</LinkButton>
        <LinkButton onChangeHandler={(event: React.FormEvent<HTMLButtonElement>) => {
          const validations = validate();
          const inValid = Object.keys(validations).map((key) => {
            return validations[key];
          }).filter(Boolean).length > 0;

          if (!inValid) {
            if (permissions) {
              const permissionsToUpdate: any[] = [];
              permissions.map((category, categoryIndex) => {
                category.products.map((product, productIndex) => {
                  if (product.permissions.hasLeadPermission !== tempPermissions[categoryIndex].products[productIndex].permissions.hasLeadPermission ||
                    product.permissions.hasContractPermission !== tempPermissions[categoryIndex].products[productIndex].permissions.hasContractPermission ||
                    product.permissions.hasSampleContractPermission !== tempPermissions[categoryIndex].products[productIndex].permissions.hasSampleContractPermission) {
                    permissionsToUpdate.push(tempPermissions[categoryIndex].products[productIndex].permissions);
                  }
                });
              });

              onChange({ user: { ...tempUser, email: tempUser?.email?.toLowerCase() }, permissions: permissionsToUpdate.length > 0 ? permissionsToUpdate : null });
            } else {
              onChange({ user: { ...tempUser, email: tempUser?.email?.toLowerCase() }, permissions: null });
            }
          } else {
            showNotificationBanner({
                error: true,
                renderContent: () => {
                  return <>Bitte füllen Sie alle Rot markierten Felder aus.</>;
                },
              },
            );
          }
        }}>Änderungen speichern</LinkButton>
      </FormButtonWrapper>
    </>
  );
};
